<template>
	<div>
		<!-- Drawer + overlay -->
		<transition name="chat-drawer-fade">
			<div
				v-if="chatStore.isOpen && !chatStore.isMinimized"
				class="chat-drawer-overlay"
				@click="onOverlayClick"
			></div>
		</transition>

		<transition name="chat-drawer-slide">
			<aside
				v-if="chatStore.isOpen && !chatStore.isMinimized"
				class="chat-drawer"
				role="dialog"
				aria-label="Assistente guIAre"
				@click.stop
			>
				<!-- Header com gradient -->
				<header class="chat-drawer-header">
					<div class="chat-drawer-header-left">
						<div class="chat-drawer-header-glyph">
							<GuIAreGlyph :size="34" />
						</div>
						<div class="chat-drawer-header-text">
							<h2 class="chat-drawer-title">
								gu<span class="chat-drawer-title-ia">IA</span>re
							</h2>
							<p class="chat-drawer-subtitle">
								<span class="chat-drawer-status-dot"></span>
								Assistente de dados
							</p>
						</div>
					</div>

					<div class="chat-drawer-header-actions">
						<button
							type="button"
							class="chat-drawer-icon-btn"
							@click="falarComAtendente"
							aria-label="Falar com atendente"
							title="Falar com atendente"
						>
							<v-icon size="18" color="#e2e8f0">mdi-headset</v-icon>
						</button>
						<button
							v-if="chatStore.messages.length > 0"
							type="button"
							class="chat-drawer-icon-btn"
							@click="novaConversa"
							aria-label="Nova conversa"
							title="Nova conversa"
						>
							<v-icon size="18" color="#e2e8f0">mdi-message-plus-outline</v-icon>
						</button>
						<button
							type="button"
							class="chat-drawer-icon-btn"
							@click="minimize"
							aria-label="Minimizar"
						>
							<v-icon size="18" color="#e2e8f0">mdi-window-minimize</v-icon>
						</button>
						<button
							type="button"
							class="chat-drawer-icon-btn"
							@click="close"
							aria-label="Fechar"
						>
							<v-icon size="18" color="#e2e8f0">mdi-close</v-icon>
						</button>
					</div>
				</header>

				<!-- Conteúdo: welcome ou mensagens -->
				<div ref="scroll" class="chat-drawer-body">
					<ChatWelcome
						v-if="!chatStore.messages.length"
						:suggestions="chatStore.suggestions"
						@suggest="onSuggestion"
					/>

					<template v-else>
						<template v-for="(msg, i) in chatStore.messages">
							<ChatBubble
								:key="`${msg.id}-${i}`"
								:role="msg.role"
								:content="msg.content"
								:pending="msg.pending"
							/>

							<!-- Oferta de atendente humano quando a IA não conseguiu responder -->
							<div
								v-if="msg.offerSupport && !msg.pending"
								:key="`support-${msg.id}-${i}`"
								class="chat-support-offer"
							>
								<p class="chat-support-offer-text">
									Não consegui resolver isso por aqui. Quer falar com um
									técnico do nosso suporte?
								</p>
								<button
									type="button"
									class="chat-support-offer-btn"
									@click="falarComAtendente"
								>
									<v-icon size="16" color="#ffffff" class="mr-1">mdi-headset</v-icon>
									Falar com o suporte
								</button>
							</div>
						</template>

						<ChatToolIndicator
							v-if="chatStore.currentToolName"
							:tool-name="chatStore.currentToolName"
						/>

						<div v-if="chatStore.error" class="chat-drawer-error">
							<v-icon size="16" color="#dc2626" class="mr-1">mdi-alert-circle-outline</v-icon>
							{{ chatStore.error }}
						</div>
					</template>
				</div>

				<!-- Input -->
				<ChatInput
					ref="input"
					:disabled="chatStore.isStreaming"
					@send="onSend"
				/>
			</aside>
		</transition>

		<!-- Pílula vertical lateral quando minimizado -->
		<transition name="chat-drawer-pill">
			<button
				v-if="chatStore.isOpen && chatStore.isMinimized"
				type="button"
				class="chat-drawer-pill"
				@click="restore"
				aria-label="Abrir conversa com guIAre"
			>
				<GuIAreGlyph :size="20" />
				<span class="chat-drawer-pill-label">guIAre</span>
			</button>
		</transition>
	</div>
</template>

<script>
import chatStore, { chatActions, CONVERSATION_KEY } from '@/store/chatStore';
import chatService from '@/services/chat/chatService';
import GuIAreGlyph from './GuIAreGlyph.vue';
import ChatBubble from './ChatBubble.vue';
import ChatToolIndicator from './ChatToolIndicator.vue';
import ChatWelcome from './ChatWelcome.vue';
import ChatInput from './ChatInput.vue';
import { openTawk } from '@/utils/tawk';

export default {
	name: 'ChatDrawer',

	components: {
		GuIAreGlyph,
		ChatBubble,
		ChatToolIndicator,
		ChatWelcome,
		ChatInput,
	},

	data() {
		return { chatStore };
	},

	watch: {
		'chatStore.messages': {
			deep: true,
			handler() {
				this.$nextTick(this.scrollToBottom);
			},
		},
		'chatStore.currentToolName'() {
			this.$nextTick(this.scrollToBottom);
		},
		'chatStore.isOpen'(opened) {
			if (opened) {
				this.ensureContext();
				this.rehydrateConversation();
			}
		},
	},

	mounted() {
		// pré-carrega o contexto se o chat já estiver aberto na montagem
		if (chatStore.isOpen) {
			this.ensureContext();
			this.rehydrateConversation();
		}
	},

	methods: {
		async ensureContext() {
			if (chatStore.contextLoaded) return;
			try {
				const modulos = await chatService.getContext();
				chatActions.setSuggestionsFromModulos(modulos);
			} catch (e) {
				// fallback silencioso: chatStore.suggestions já tem o default
			}
		},

		minimize() {
			chatActions.minimize();
		},
		close() {
			chatActions.close();
		},
		restore() {
			chatActions.restore();
			this.$nextTick(this.scrollToBottom);
		},

		novaConversa() {
			chatActions.newConversation();
		},

		falarComAtendente() {
			// abre o atendimento humano (Tawk.to) sob demanda
			openTawk();
		},

		onOverlayClick() {
			// clicar fora minimiza (não fecha) — preserva a conversa
			this.minimize();
		},

		onSuggestion(text) {
			this.$refs.input?.setText(text);
		},

		async rehydrateConversation() {
			// Após um reload (F5), o estado em memória é perdido. Se houver uma
			// conversa salva, recarrega as mensagens do backend pra não perder o fio.
			if (chatStore.conversationUuid || chatStore.messages.length) return;

			const uuid = localStorage.getItem(CONVERSATION_KEY);
			if (!uuid) return;

			try {
				const { conversation, messages } = await chatService.loadMessages(uuid);
				if (conversation && Array.isArray(messages) && messages.length) {
					chatActions.setConversation(conversation);
					chatActions.setMessages(messages);
					this.$nextTick(this.scrollToBottom);
				} else {
					localStorage.removeItem(CONVERSATION_KEY);
				}
			} catch (e) {
				// conversa inexistente/sem acesso (ex.: trocou de usuário) — limpa
				localStorage.removeItem(CONVERSATION_KEY);
			}
		},

		async ensureConversation() {
			if (chatStore.conversationUuid) return;
			try {
				const conv = await chatService.startConversation();
				chatActions.setConversation(conv);
			} catch (e) {
				chatActions.setError('Não consegui iniciar a conversa agora. Tenta de novo.');
			}
		},

		async onSend(content) {
			await this.ensureConversation();
			if (!chatStore.conversationUuid) return;

			chatActions.pushUserMessage(content);
			const idx = chatActions.startAssistantMessage();
			this.$nextTick(this.scrollToBottom);

			await chatService.streamMessage(chatStore.conversationUuid, content, {
				onDelta: (delta) => chatActions.appendAssistantDelta(idx, delta),
				onToolStart: (name) => {
					chatActions.setToolRunning(name);
					// A IA sinalizou que não conseguiu responder: oferecer atendente.
					if (name === 'registrar_pergunta_nao_atendida') {
						chatActions.markSupportOffer(idx);
					}
				},
				onToolEnd: () => chatActions.clearToolRunning(),
				onDone: (payload) => chatActions.finishAssistantMessage(idx, payload),
				onError: (msg) => {
					chatActions.setError(msg);
					chatActions.finishAssistantMessage(idx);
				},
			});
		},

		scrollToBottom() {
			const el = this.$refs.scroll;
			if (el) el.scrollTop = el.scrollHeight;
		},
	},
};
</script>

<style scoped>
.chat-drawer-overlay {
	position: fixed;
	inset: 0;
	background: rgba(15, 23, 42, 0.18);
	backdrop-filter: blur(2px);
	z-index: 1200;
}

.chat-drawer {
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	width: 440px;
	max-width: 100vw;
	background: rgba(248, 250, 252, 0.92);
	backdrop-filter: blur(20px);
	box-shadow: -24px 0 48px -24px rgba(15, 23, 42, 0.25);
	border-left: 1px solid rgba(148, 163, 184, 0.35);
	display: flex;
	flex-direction: column;
	z-index: 1201;
}

.chat-drawer-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 18px;
	min-height: 72px;
	background:
		radial-gradient(
			circle at 0% 0%,
			rgba(var(--color-primary-rgb, 252, 207, 50), 0.18) 0%,
			transparent 45%
		),
		linear-gradient(135deg, #0b1532 0%, #1e293b 55%, #0f172a 100%);
	border-bottom: 1px solid rgba(var(--color-primary-rgb, 252, 207, 50), 0.18);
	position: relative;
}

.chat-drawer-header-glyph {
	filter: drop-shadow(0 0 14px rgba(var(--color-primary-rgb, 252, 207, 50), 0.45));
}

.chat-drawer-header-left {
	display: flex;
	align-items: center;
	gap: 12px;
}

.chat-drawer-header-text {
	display: flex;
	flex-direction: column;
}

.chat-drawer-title {
	margin: 0;
	font-size: 18px;
	font-weight: 800;
	letter-spacing: -0.01em;
	line-height: 1.1;
	color: #f8fafc;
}

.chat-drawer-title-ia {
	background: linear-gradient(
		135deg,
		rgba(var(--color-primary-rgb, 252, 207, 50), 1) 0%,
		rgba(var(--color-primary-rgb, 252, 207, 50), 0.85) 100%
	);
	-webkit-background-clip: text;
	background-clip: text;
	color: transparent;
	padding: 0 1px;
	text-shadow: 0 0 18px rgba(var(--color-primary-rgb, 252, 207, 50), 0.25);
	font-weight: 900;
}

.chat-drawer-subtitle {
	margin: 2px 0 0;
	font-size: 12px;
	font-weight: 500;
	display: flex;
	align-items: center;
	gap: 6px;
	color: #cbd5e1;
}

.chat-drawer-status-dot {
	width: 7px;
	height: 7px;
	background: #22c55e;
	border-radius: 50%;
	box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.25);
	animation: status-pulse 2s ease-in-out infinite;
}

@keyframes status-pulse {
	0%, 100% { box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.25); }
	50%      { box-shadow: 0 0 0 6px rgba(34, 197, 94, 0.05); }
}

.chat-drawer-header-actions {
	display: flex;
	gap: 4px;
}

.chat-drawer-icon-btn {
	width: 30px;
	height: 30px;
	border: none;
	background: rgba(255, 255, 255, 0.08);
	border-radius: 8px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: background 0.15s ease;
}

.chat-drawer-icon-btn:hover {
	background: rgba(var(--color-primary-rgb, 252, 207, 50), 0.2);
}

.chat-drawer-body {
	flex: 1;
	overflow-y: auto;
	padding: 16px;
	scroll-behavior: smooth;
}

.chat-drawer-error {
	margin: 8px 0 4px 40px;
	padding: 8px 12px;
	background: #fef2f2;
	border: 1px solid #fecaca;
	border-radius: 12px;
	color: #b91c1c;
	font-size: 13px;
	display: flex;
	align-items: center;
}

.chat-support-offer {
	margin: 4px 0 10px 40px;
	padding: 12px 14px;
	background: rgba(30, 58, 138, 0.06);
	border: 1px solid rgba(30, 58, 138, 0.15);
	border-radius: 12px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 10px;
}

.chat-support-offer-text {
	margin: 0;
	font-size: 13px;
	line-height: 1.5;
	color: #334155;
}

.chat-support-offer-btn {
	display: inline-flex;
	align-items: center;
	padding: 8px 14px;
	border: none;
	border-radius: 10px;
	background: linear-gradient(135deg, #0b1532 0%, #1e3a8a 100%);
	color: #ffffff;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
	transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.chat-support-offer-btn:hover {
	transform: translateY(-1px);
	box-shadow: 0 10px 20px -12px rgba(30, 58, 138, 0.7);
}

/* Pílula lateral quando minimizado */
.chat-drawer-pill {
	position: fixed;
	top: 50%;
	right: 0;
	transform: translateY(-50%);
	border: 1px solid rgba(var(--color-primary-rgb, 252, 207, 50), 0.35);
	border-right: none;
	border-top-left-radius: 14px;
	border-bottom-left-radius: 14px;
	padding: 12px 10px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
	cursor: pointer;
	z-index: 1199;
	background: linear-gradient(135deg, #0b1532 0%, #1e293b 55%, #0f172a 100%);
	color: #f8fafc;
	box-shadow: -8px 0 20px -8px rgba(15, 23, 42, 0.5);
	transition: transform 0.15s ease, padding 0.15s ease, border-color 0.15s ease;
}

.chat-drawer-pill:hover {
	transform: translateY(-50%) translateX(-2px);
	border-color: rgba(var(--color-primary-rgb, 252, 207, 50), 0.7);
}

.chat-drawer-pill-label {
	font-size: 10px;
	font-weight: 800;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	writing-mode: vertical-rl;
	transform: rotate(180deg);
	color: #f8fafc;
}

/* Transições */
.chat-drawer-fade-enter-active,
.chat-drawer-fade-leave-active {
	transition: opacity 220ms ease;
}
.chat-drawer-fade-enter-from,
.chat-drawer-fade-leave-to {
	opacity: 0;
}

.chat-drawer-slide-enter-active,
.chat-drawer-slide-leave-active {
	transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease;
}
.chat-drawer-slide-enter-from,
.chat-drawer-slide-leave-to {
	transform: translateX(40px);
	opacity: 0;
}

.chat-drawer-pill-enter-active,
.chat-drawer-pill-leave-active {
	transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease;
}
.chat-drawer-pill-enter-from,
.chat-drawer-pill-leave-to {
	transform: translate(20px, -50%);
	opacity: 0;
}

/* Vue 2 compatibility (sem -from suffix em todas versões) */
.chat-drawer-fade-enter, .chat-drawer-fade-leave-to { opacity: 0; }
.chat-drawer-slide-enter, .chat-drawer-slide-leave-to { transform: translateX(40px); opacity: 0; }
.chat-drawer-pill-enter, .chat-drawer-pill-leave-to { transform: translate(20px, -50%); opacity: 0; }

/* Responsivo */
@media (max-width: 1023px) and (min-width: 768px) {
	.chat-drawer {
		width: 60vw;
	}
}

@media (max-width: 767px) {
	.chat-drawer {
		width: 100vw;
		border-left: none;
	}

	.chat-drawer-overlay {
		display: none;
	}
}
</style>
